import { useEffect, useState } from "react";
import { Box, Button, Divider, IconButton, Tooltip, useTheme } from "@mui/material";
import MediaSettings from "../media-settings/Widget";
import { ChatStateButton, ParticipantStateButton } from "./sidebar/SidebarControls";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InfoButton from "./InfoButton";
import { formatDuration, useSettings } from "../tools";
import { MeetingPageStage } from "../types";
import { SelectedMediaDevices, useSession, useSipClient, DeviceSelector, SessionState } from "@dvrt/web-phone-client";
import RaiseHandButton from "./RaiseHandButton";
import DesktopIcon from "../../svg-icons/DesktopIcon";
import { useInterval } from "@dvrt/react-tools";
import moment, { Duration, duration } from "moment";
import { ControlBarIconContainer } from "conference/components/meeting-page/room/widgets";
import { useParticipantStatus } from "conference/components/meeting-page/room/participantStatus";
import { useCurrentMeeting } from "conference/components/meeting-page/contexts/currentMeeting";
import { defineMessage, defineMessages, FormattedMessage } from "react-intl";

const screenShareMessages = defineMessages({
    on: {
        id: "meetingPage.room.ds.on",
        defaultMessage: "You are sharing your desktop, click the icon to stop.",
    },
    off: {
        id: "meetingPage.room.ds.off",
        defaultMessage: "Click here to share your desktop.",
    },
});

const fullscreenMessage = defineMessage({
    id: "meetingPage.room.fullscreen",
    defaultMessage: "{on, plural, =0 {Go} other {Exit}} fullscreen",
});

const DesktopShareButton = () => {
    const context = useSession();

    return (
        <Tooltip
            title={
                <FormattedMessage {...(context.localScreenShare ? screenShareMessages.on : screenShareMessages.off)} />
            }
            arrow
        >
            <IconButton
                onClick={() => {
                    context.manager.toggleScreenShare(window.desktopShareConstraints);
                }}
                disabled={context.state !== SessionState.connected}
                color={context.localScreenShare ? "primary" : "default"}
                size="large"
            >
                <DesktopIcon
                    style={{ width: 24, height: 24 }}
                    color={context.localScreenShare ? "primary" : "default"}
                    filled={context.localScreenShare}
                />
            </IconButton>
        </Tooltip>
    );
};

const BarDivider = () => <Divider orientation="vertical" flexItem style={{ margin: 5 }} />;

const CallTime = () => {
    const sipClient = useSipClient();
    const [callDuration, setCallDuration] = useState<Duration>(null);

    useInterval(() => {
        setCallDuration(duration(moment().diff(moment(sipClient.session?.start_time))));
    }, 1000);

    return <div style={{ marginRight: "1em" }}>{callDuration ? formatDuration(callDuration) : ""}</div>;
};

const FullScreenButton = () => {
    const [isFullscreen, setFullScreen] = useState(false);

    const toggleFullScreen = () => {
        setFullScreen(!isFullscreen);
        if (isFullscreen) document.exitFullscreen();
        else document.documentElement.requestFullscreen();
    };

    return (
        <Tooltip title={<FormattedMessage {...fullscreenMessage} values={{ on: isFullscreen ? 1 : 0 }} />} arrow>
            <IconButton onClick={toggleFullScreen} size="large">
                <ControlBarIconContainer>
                    <FontAwesomeIcon icon={isFullscreen ? "compress" : "expand"} />
                </ControlBarIconContainer>
            </IconButton>
        </Tooltip>
    );
};

const Header = () => {
    const theme = useTheme();
    const settings = useSettings();
    const sipClient = useSipClient();

    const onLeave = () => {
        settings.stage = MeetingPageStage.LiftOff;
    };

    const onSaveDevices = (devs: SelectedMediaDevices) => {
        sipClient.selectDevices(devs);
    };

    const currentMeeting = useCurrentMeeting();
    const { unmuteAllowed, isHost } = useParticipantStatus(currentMeeting.selfUri);

    const allowUnmute = unmuteAllowed || isHost;

    useEffect(() => {
        if (!allowUnmute) settings.audio = false;
    }, [allowUnmute]);

    return (
        <Box
            sx={{
                display: "flex",
                width: "100%",
                padding: "0.2em",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Box sx={{ width: "25%", display: "flex", alignItems: "center" }}>
                <InfoButton />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "50%",
                }}
            >
                <MediaSettings allowUnmute={allowUnmute} />
                <DeviceSelector onSave={onSaveDevices} />
                <BarDivider />
                <DesktopShareButton />
                <BarDivider />
                <ChatStateButton />
                <ParticipantStateButton />
                <BarDivider />
                <RaiseHandButton />
                <FullScreenButton />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "25%",
                    paddingRight: "0.5em",
                    alignItems: "center",
                }}
            >
                <CallTime />
                <Button variant="contained" color="secondary" onClick={onLeave}>
                    <FontAwesomeIcon icon="phone" style={{ transform: "rotate(225deg)", marginRight: "1em" }} />
                    <FormattedMessage id="meetingPage.room.leave" defaultMessage="Leave" />
                </Button>
            </Box>
        </Box>
    );
};

export default Header;
